// =============================================================================
// connection-classifier.ts – Unified connection classification logic
// =============================================================================

import type { AppState, AppConfig } from "./types.js";

export interface ConnectionThresholds {
  tightMin: number;
  goodMin: number;
  longMin: number;
}

export interface ConnectionState {
  label: string;
  css: string;
}

/**
 * Classifies a connection quality.
 * @pre thresholds.tightMin < thresholds.goodMin < thresholds.longMin
 * @post returns one of exactly 5 states
 */
export function classifyConnection(
  waitMin: number,
  thresholds: ConnectionThresholds
): ConnectionState {
  if (waitMin < 0) {
    return { label: "perso", css: "missed" };
  }
  if (waitMin < thresholds.tightMin) {
    return { label: "stretta", css: "tight" };
  }
  if (waitMin <= thresholds.goodMin) {
    return { label: "buona", css: "good" };
  }
  if (waitMin <= thresholds.longMin) {
    return { label: "comoda", css: "calm" };
  }
  return { label: "lunga", css: "long" };
}

/**
 * Retrieves connection thresholds from settings with fallback to defaults.
 */
export function getThresholdsFromState(
  state: AppState | null | undefined,
  cfg: AppConfig
): ConnectionThresholds {
  const tight = state?.settings?.connectionTightMin ?? cfg?.defaults?.connectionTightMin ?? 4;
  const good = state?.settings?.connectionGoodMin ?? cfg?.defaults?.connectionGoodMin ?? 12;
  const long = state?.settings?.connectionLongMin ?? cfg?.defaults?.connectionLongMin ?? 25;

  return {
    tightMin: tight,
    goodMin: good,
    longMin: long
  };
}
